import { Box, Button, Flex, Grid, Text, useDisclosure, useToast, VStack } from "@chakra-ui/react";
import * as React from "react";
import { Fragment, useCallback, useMemo, useRef, useState } from "react";
import { dimensionsKeys, showErrorToast } from "../../../../utils";
import { DimensionsGridHeaderRow, DimensionsGridItems } from "./index";
import DimensionsGridFilterBar from "./DimensionsGridFilterBar";
import { CONSUMPTION_ITEMS, MEASURES } from "../../helpers";
import { useTranslation } from "react-i18next";
import { Paginator } from "../../../../components/Paginator";
import { DownloadSimple, FileArrowUp, PencilSimpleLine, Plus, Sparkle, UploadSimple, X } from "@phosphor-icons/react";
import FileDragDrop from "../../../../components/FileDragDrop";
import { ConfirmationDialog } from "../../../../components/ConfirmationDialog";
import { useAuth } from "../../../../context";
import { isAllowedToView } from "../../../../utils/AuthUtils";
import { permissions } from "../../../../config/paths";
import MODULES from "../../../../config/modules";
import { targetForSection } from "../../aiIngestTarget";
import AiMappingModal from "../../../aiDataMapping/components/AiMappingModal";

// Static columns are 'Category', 'Value' and 'Measure'
export const NUMBER_OF_STATIC_COLS = 3;

// Action column contains the action buttons
export const NUMBER_OF_ACTION_COLS = 1;

function DimensionsGrid({
                          questionnaireId, scope, organizationStructure, sectionType, consumptionTypesAttr,
                          handleAddItem, handleEditItem, handleDeleteItem, handleClearAll, getItemsFunction, isPreview, onDataItemsChange,
                          onDownloadTemplate, importBtnId, onImport
                        }) {
  const { t } = useTranslation("translation");
  const toast = useToast();

  const [itemsList, setItemsList] = useState([]);
  // Client-side pagination over the filtered list. We fetch the full dataset
  // once (size=9999) so every filter can see every row, not just the page
  // the server happened to return.
  const [pageIndex, setPageIndex] = useState(0);
  const [queryPageSize, setQueryPageSize] = useState(10);
  // { [columnKey]: Array<{ label, value }> } — matches the MultiSelectFilter
  // shape used by the Tasks list. In-memory only (no session persistence).
  const [filters, setFilters] = useState({});
  // null = unsorted (default order preserved). Otherwise { key, direction }.
  // Currently only the Value column is sortable.
  const [sortState, setSortState] = useState(null);

  const toggleValueSort = () => {
    setSortState((prev) => {
      if (!prev || prev.key !== "value") return { key: "value", direction: "asc" };
      if (prev.direction === "asc") return { key: "value", direction: "desc" };
      return null;
    });
  };

  const prepareSectionValues = useCallback(items => {
    return items.map(item => {
      const customItem = {
        id: item.id ?? null
      };

      dimensionsKeys.forEach(dimKey => {
        // Check if dimension is used for the current questionnaire
        if (organizationStructure?.dimensions[dimKey]) {
          customItem[`${dimKey}Item`] = item.dimensionItems[`${dimKey}Item`] ?? "";
        }
      });

      if (scope === "one") customItem["consumptionType"] = item.consumptionTypeId ?? "";
      else customItem["electricityType"] = item.electricityType ?? "";
      customItem["consumptionValue"] = item.consumption?.value ?? "";
      customItem["consumptionMeasure"] = item.consumption?.measureId ?? "";

      return customItem;
    });
  }, [organizationStructure, scope]);

  getItemsFunction({
      queryParams: {
        page: 0, size: 9999, sort: "id,desc"
      },
      type: sectionType,
      questionnaireId: questionnaireId
    },
    {
      onSuccess: (page) => {
        const pageContent = page?.content ?? [];
        const items = prepareSectionValues(pageContent);
        setItemsList(items);
        onDataItemsChange(consumptionTypesAttr, items.length);
      },
      onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      cacheTime: 0
    }
  );

  // Label resolvers — duplicated from DimensionsGridItems so the filter
  // options read the same strings the user sees on each row. Keeps the
  // filter-option list consistent with what's actually rendered.
  const getDimensionLabel = useCallback(
    (dimKey, id) => organizationStructure?.dimensions?.[dimKey]?.items?.find(el => el.id === id)?.name ?? "",
    [organizationStructure]
  );
  const getConsumptionTypeLabel = useCallback((id) => {
    if (scope !== "one") return "";
    const ct = organizationStructure?.[consumptionTypesAttr]?.find(el => el.id === id);
    return CONSUMPTION_ITEMS[ct?.code] ?? ct?.name ?? "";
  }, [organizationStructure, scope, consumptionTypesAttr]);
  const getElectricityTypeLabel = useCallback(
    (key) => CONSUMPTION_ITEMS[key] ?? key ?? "",
    []
  );
  const getMeasureLabel = useCallback((item) => {
    if (scope === "one") {
      const measure = organizationStructure?.measures?.find(m => m.consumptionTypeId === parseInt(item.consumptionType))
        ?.measures?.find(m => m.id === parseInt(item.consumptionMeasure));
      return MEASURES[measure?.code] ?? measure?.name ?? "";
    }
    const measure = organizationStructure?.electricityMeasures
      ?.find(m => m.electricityType === item.electricityType)
      ?.measures?.find(m => m.id === parseInt(item.consumptionMeasure));
    return MEASURES[measure?.code] ?? measure?.name ?? "";
  }, [organizationStructure, scope]);

  // Annotate each row with display labels + the "category" column key used
  // for both the visible cell and the filter. Keeps filter matching simple
  // (compare labels-to-labels) without duplicating the resolver logic
  // everywhere.
  const decoratedItems = useMemo(() => itemsList.map((item) => {
    const decorated = { ...item };
    dimensionsKeys.forEach((dimKey) => {
      if (organizationStructure?.dimensions?.[dimKey]) {
        decorated[`__label_${dimKey}`] = getDimensionLabel(dimKey, item[`${dimKey}Item`]);
      }
    });
    decorated.__label_category = scope === "one"
      ? getConsumptionTypeLabel(item.consumptionType)
      : getElectricityTypeLabel(item.electricityType);
    decorated.__label_value = item.consumptionValue != null && item.consumptionValue !== ""
      ? String(item.consumptionValue)
      : "";
    decorated.__label_measure = getMeasureLabel(item);
    return decorated;
  }), [itemsList, organizationStructure, scope, getDimensionLabel, getConsumptionTypeLabel, getElectricityTypeLabel, getMeasureLabel]);

  // Build filter config dynamically so callers don't have to hardcode which
  // columns exist. Dimensions are rolled up into a single "Dimension" pill
  // (matching the Tasks list filter bar); the remaining columns are exposed
  // as individual pills.
  //
  // Each dimension group lists the distinct display labels present in the
  // current dataset for that dimension.
  const dimensionGroups = useMemo(() => {
    const groups = [];
    dimensionsKeys.forEach((dimKey) => {
      if (!organizationStructure?.dimensions?.[dimKey]) return;
      const labels = new Set();
      decoratedItems.forEach((row) => {
        const label = row[`__label_${dimKey}`];
        if (label) labels.add(label);
      });
      const items = [...labels].sort().map((label) => ({ label, value: label }));
      if (items.length > 0) {
        groups.push({
          dimKey,
          name: organizationStructure.dimensions[dimKey].name,
          items,
        });
      }
    });
    return groups;
  }, [decoratedItems, organizationStructure]);

  // Non-dimension columns — driven by whatever labels are present rather than
  // a hardcoded list, so adding a new column to a row automatically gets a
  // pill once any label lands in __label_<key>.
  const extraFilterColumns = useMemo(() => {
    // Value is deliberately excluded — filtering on exact numeric values
    // doesn't add discovery. Users sort by value instead (see sortState).
    const columnLabels = {
      category: t("organizationQuestionnaire.dimensionsGrid.category"),
      measure: t("organizationQuestionnaire.dimensionsGrid.measure"),
    };
    return Object.entries(columnLabels)
      .map(([key, label]) => {
        const labels = new Set();
        decoratedItems.forEach((row) => {
          const v = row[`__label_${key}`];
          if (v) labels.add(v);
        });
        const options = [...labels].sort().map((v) => ({ label: v, value: v }));
        return options.length > 0 ? { key, label, options } : null;
      })
      .filter(Boolean);
  }, [decoratedItems, t]);

  const filteredItems = useMemo(() => {
    const dimensionSelections = filters._dimension || [];
    const activeExtras = Object.entries(filters).filter(
      ([key, vals]) => key !== "_dimension" && Array.isArray(vals) && vals.length > 0
    );
    if (dimensionSelections.length === 0 && activeExtras.length === 0) return decoratedItems;

    return decoratedItems.filter((row) => {
      // Dimension selections can span multiple dimension levels. A row passes
      // if every dimension level that has any selections contains at least
      // one selected item that matches the row's label for that level.
      if (dimensionSelections.length > 0) {
        const byDim = dimensionSelections.reduce((acc, sel) => {
          (acc[sel.dimKey] ||= []).push(sel.label);
          return acc;
        }, {});
        for (const [dimKey, labels] of Object.entries(byDim)) {
          const rowLabel = row[`__label_${dimKey}`];
          if (!labels.includes(rowLabel)) return false;
        }
      }

      for (const [key, vals] of activeExtras) {
        const rowLabel = row[`__label_${key}`];
        if (!vals.some((v) => v.value === rowLabel)) return false;
      }

      return true;
    });
  }, [decoratedItems, filters]);

  // Sum the value column across the filtered set, grouped by measure unit.
  // Mixing units (e.g. litres + kWh) in a single total is meaningless, so we
  // expose one sub-total per measure. `value` is parsed defensively because
  // it arrives from the API as a string in some cases.
  const filteredTotals = useMemo(() => {
    const byMeasure = new Map();
    filteredItems.forEach((row) => {
      const n = Number(row.consumptionValue);
      if (!Number.isFinite(n)) return;
      const measure = row.__label_measure || "";
      byMeasure.set(measure, (byMeasure.get(measure) || 0) + n);
    });
    return [...byMeasure.entries()]
      .map(([measure, value]) => ({ measure, value }))
      .sort((a, b) => b.value - a.value);
  }, [filteredItems]);

  const sortedItems = useMemo(() => {
    if (!sortState || sortState.key !== "value") return filteredItems;
    const dir = sortState.direction === "asc" ? 1 : -1;
    const toNumber = (row) => {
      const raw = row.consumptionValue;
      const n = Number(raw);
      return Number.isFinite(n) ? n : -Infinity;
    };
    return [...filteredItems].sort((a, b) => (toNumber(a) - toNumber(b)) * dir);
  }, [filteredItems, sortState]);

  const totalPages = Math.max(1, Math.ceil(sortedItems.length / queryPageSize));
  // Clamp the page when filters shrink the list below the current page.
  const safePageIndex = Math.min(pageIndex, totalPages - 1);
  const pageSlice = useMemo(
    () => sortedItems.slice(safePageIndex * queryPageSize, (safePageIndex + 1) * queryPageSize),
    [sortedItems, safePageIndex, queryPageSize]
  );

  const handlePageSelection = (newPage) => setPageIndex(newPage - 1);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPageIndex(0);
  };

  const clearAllFilters = () => { setFilters({}); setPageIndex(0); };

  const colCount = (dimensionsKeys.filter(dimKey =>
    organizationStructure.dimensions[dimKey] !== null).length + NUMBER_OF_STATIC_COLS) * 2 + (!isPreview ? NUMBER_OF_ACTION_COLS : 0);

  const [activeMethod, setActiveMethod] = useState("direct");
  const [templateFile, setTemplateFile] = useState(null);
  const fileInputRef = useRef(null);

  // AI Data Mapping's workspace is a separate full-viewport page reached by deep link, but session
  // pick/create/delete now happens in an overlay right here. The button is gated with the SAME check as
  // the route (paths.js) — a button that appears where the route refuses is worse than no button.
  const { userPermissions } = useAuth();
  const aiTarget = targetForSection(scope, sectionType);
  const canUseAiMapping = Boolean(aiTarget)
    && isAllowedToView(userPermissions, permissions.aiDataMapping, [MODULES.DataMapping.id]);

  const {
    isOpen: isClearAllDialogOpen,
    onOpen: onClearAllDialogOpen,
    onClose: onClearAllDialogClose,
  } = useDisclosure();
  const {
    isOpen: isAiMappingOpen,
    onOpen: onAiMappingOpen,
    onClose: onAiMappingClose,
  } = useDisclosure();
  const handleClearAllConfirm = () => {
    onClearAllDialogClose();
    handleClearAll?.(filteredItems);
  };

  return (
    <Fragment>
      {!isPreview && (
        <Flex gap={2} mb={4} align="center" flexWrap="wrap">
          <Button
            size="sm"
            variant="outline"
            borderRadius="full"
            borderColor={activeMethod === "direct" ? "lime" : "limeShade.600"}
            bg={activeMethod === "direct" ? "lightLime" : "transparent"}
            color="limeShade.600"
            leftIcon={<PencilSimpleLine size={14} />}
            onClick={() => { setActiveMethod("direct"); handleAddItem(); }}
            _hover={{ bg: "lightLime", borderColor: "lime" }}
          >
            + {t("buttons.addNewItems")}
          </Button>
          <Button
            size="sm"
            variant="outline"
            borderRadius="full"
            borderColor={activeMethod === "import" ? "lime" : "limeShade.600"}
            bg={activeMethod === "import" ? "lightLime" : "transparent"}
            color="limeShade.600"
            leftIcon={<FileArrowUp size={14} />}
            onClick={() => setActiveMethod(activeMethod === "import" ? "direct" : "import")}
            _hover={{ bg: "lightLime", borderColor: "lime" }}
          >
            Import from Template
          </Button>
          {canUseAiMapping && (
          <Button
            size="sm"
            variant="outline"
            borderRadius="full"
            borderColor="limeShade.600"
            bg="transparent"
            color="limeShade.600"
            leftIcon={<Sparkle size={14} />}
            onClick={onAiMappingOpen}
            _hover={{ bg: "lightLime", borderColor: "lime" }}
          >
            AI Mapping
          </Button>
          )}
        </Flex>
      )}
      {canUseAiMapping && (
        <AiMappingModal
          isOpen={isAiMappingOpen}
          onClose={onAiMappingClose}
          questionnaireId={questionnaireId}
          target={aiTarget}
        />
      )}
      {activeMethod === "import" && !isPreview && (
        <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={6} mb={4} bg="gray.50" position="relative">
          <Flex justify="space-between" align="flex-start" mb={4} gap={2}>
            <Box>
              <Text fontSize="14px" fontWeight="600" color="gray.700">Import from Template</Text>
              <Text fontSize="13px" color="gray.500" mt={1}>Download a template, fill in your data, then drag and drop or select the file to import.</Text>
            </Box>
            <Box
              as="button"
              aria-label="Cancel import"
              color="gray.400"
              _hover={{ color: "gray.700" }}
              onClick={() => { setActiveMethod("direct"); setTemplateFile(null); }}
              mt={-1}
            >
              <X size={18} />
            </Box>
          </Flex>
          <FileDragDrop onFiles={(files) => { if (files?.[0]) setTemplateFile(files[0]); }} borderRadius="lg">
            <VStack
              border="2px dashed"
              borderColor={templateFile ? "lime" : "gray.300"}
              borderRadius="lg"
              py={6}
              px={4}
              spacing={2}
              cursor="pointer"
              bg={templateFile ? "lightLime" : "white"}
              _hover={{ borderColor: "lime", bg: "lightLime" }}
              transition="all 0.15s"
              onClick={() => fileInputRef.current?.click()}
            >
              <UploadSimple size={28} color={templateFile ? "#85C22A" : "#A0AEC0"} />
              {templateFile ? (
                <Text fontSize="13px" fontWeight="600" color="gray.700">{templateFile.name}</Text>
              ) : (
                <>
                  <Text fontSize="13px" fontWeight="600" color="gray.600">Drop file here or click to browse</Text>
                  <Text fontSize="11px" color="gray.400">.xlsx, .xls</Text>
                </>
              )}
            </VStack>
          </FileDragDrop>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept=".xlsx,.xls"
            onChange={(e) => {
              if (e.target.files?.[0]) setTemplateFile(e.target.files[0]);
              e.target.value = null;
            }}
          />
          <Flex justify="space-between" align="center" mt={3}>
            {onDownloadTemplate ? (
              <Button
                size="sm"
                variant="outline"
                borderRadius="full"
                borderColor="limeShade.600"
                color="limeShade.600"
                leftIcon={<DownloadSimple size={14} />}
                onClick={onDownloadTemplate}
              >
                Download Template
              </Button>
            ) : <Box />}
            {templateFile && (
              <Flex gap={2}>
                <Button
                  size="sm"
                  variant="ghost"
                  color="gray.400"
                  onClick={() => setTemplateFile(null)}
                  _hover={{ color: "gray.600" }}
                >
                  Remove
                </Button>
                <Button
                  size="sm"
                  bg="lime"
                  color="pureBlack"
                  borderRadius="full"
                  leftIcon={<UploadSimple size={14} />}
                  _hover={{ bg: "limeShade.300" }}
                  onClick={() => {
                    if (onImport) {
                      const fakeEvent = { preventDefault: () => {}, target: { files: [templateFile] } };
                      onImport(fakeEvent, importBtnId);
                    }
                    setTemplateFile(null);
                  }}
                >
                  Import
                </Button>
              </Flex>
            )}
          </Flex>
        </Box>
      )}
      {itemsList.length > 0 && (
        <DimensionsGridFilterBar
          dimensionGroups={dimensionGroups}
          columns={extraFilterColumns}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearAll={clearAllFilters}
          filteredCount={filteredItems.length}
          totalCount={itemsList.length}
        />
      )}

      {itemsList.length > 0 &&
        <Box borderWidth="1px" borderRadius="md" overflow="hidden" bg="white" mb={4}>
          <Grid
            templateColumns={`repeat(${colCount}, 1fr)`}
            minWidth="700"
          >
            <DimensionsGridHeaderRow
              organizationStructure={organizationStructure}
              scope={scope}
              consumptionTypesAttr={consumptionTypesAttr}
              permanentColumns={!isPreview ? [
                t("organizationQuestionnaire.dimensionsGrid.category"),
                t("organizationQuestionnaire.dimensionsGrid.value"),
                t("organizationQuestionnaire.dimensionsGrid.measure"),
                ""
              ] : [
                t("organizationQuestionnaire.dimensionsGrid.category"),
                t("organizationQuestionnaire.dimensionsGrid.value"),
                t("organizationQuestionnaire.dimensionsGrid.measure")
              ]}
              isPreview={isPreview}
              sortState={sortState}
              onToggleValueSort={toggleValueSort}
              onClearAll={handleClearAll && filteredItems.length > 0 ? onClearAllDialogOpen : undefined}
            />
            <DimensionsGridItems
              items={pageSlice}
              handleEditItem={handleEditItem}
              handleDeleteItem={handleDeleteItem}
              organizationStructure={organizationStructure}
              scope={scope}
              consumptionTypesAttr={consumptionTypesAttr}
              isPreview={isPreview}
            />
            {filteredTotals.length > 0 && filteredTotals.map((row, idx) => (
              <Box
                key={`total-${row.measure}-${idx}`}
                gridColumn={`1 / -1`}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                px={4}
                py={2}
                bg="lightLime"
                borderTop={idx === 0 ? "2px solid" : "1px solid"}
                borderColor="gray.200"
              >
                <Text fontSize="13px" fontWeight="600" color="gray.600">
                  {idx === 0
                    ? (filteredItems.length === itemsList.length ? "Total" : "Filtered total")
                    : ""}
                </Text>
                <Text fontSize="13px" fontWeight="700" color="gray.800">
                  {row.value.toLocaleString("en-GB", { maximumFractionDigits: 2 })}
                  {row.measure ? ` ${row.measure}` : ""}
                </Text>
              </Box>
            ))}
          </Grid>
        </Box>}

      {itemsList.length === 0 && (
        <Box
          border="1px dashed"
          borderColor="gray.300"
          borderRadius="md"
          py={8}
          mb={4}
          textAlign="center"
        >
          <Text fontSize="14px" color="gray.400">
            No activity data added yet.{!isPreview && " Click \"+ Add New Items\" to get started."}
          </Text>
        </Box>
      )}

      {itemsList.length > 0 && filteredItems.length === 0 && (
        <Box
          border="1px dashed"
          borderColor="gray.300"
          borderRadius="md"
          py={8}
          mb={4}
          textAlign="center"
        >
          <Text fontSize="14px" color="gray.400">
            No items match the current filters.
          </Text>
        </Box>
      )}

      {filteredItems.length > 0 &&
        <Flex justifyContent={isPreview ? "center" : "start"} mb={4}>
          <Paginator
            selectedPage={safePageIndex + 1}
            numberOfPages={totalPages}
            handlePageSelection={(newPage) => handlePageSelection(newPage)}
            pageSize={queryPageSize}
            onPageSizeChange={(newSize) => { setQueryPageSize(newSize); setPageIndex(0); }}
            totalCount={filteredItems.length}
          />
        </Flex>}

      <ConfirmationDialog
        isOpen={isClearAllDialogOpen}
        onClose={onClearAllDialogClose}
        onConfirm={handleClearAllConfirm}
        headerText="Clear data?"
        confirmationText={
          filteredItems.length === itemsList.length
            ? `This will permanently delete all ${filteredItems.length} ${filteredItems.length === 1 ? "row" : "rows"} in this table. This cannot be undone.`
            : `This will permanently delete the ${filteredItems.length} ${filteredItems.length === 1 ? "row" : "rows"} matching the current filters (out of ${itemsList.length} total). This cannot be undone.`
        }
      />
    </Fragment>
  );
}

export default DimensionsGrid;